import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  FileCheck2,
  FolderOpen,
  Loader2,
  Lock,
  Users,
} from "lucide-react";
import html2pdf from "html2pdf.js";
import markdownit from "markdown-it";
import { Page } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { incidentService } from "@/services/incidentService";
import { useDistrictConfig } from "./restoration/DistrictConfig";
import { IncidentDashboard } from "./restoration/IncidentDashboard";
import {
  RestorationRoomChat,
  type ChatTabStage,
  type RestorationRoomChatHandle,
} from "./restoration/RestorationRoomChat";
import { StageTracker } from "./restoration/StageTracker";
import { IncidentDocumentsPanel } from "./restoration/IncidentDocumentsPanel";
import {
  storageService,
  type FileUpload,
  type StorageUserFile,
} from "@/services/storageService";
import {
  type GradeLevel,
  type IncidentStatus,
  type Incident,
  type ShareScope,
  DEFAULT_INCIDENT,
  effectiveGrade,
  formatChosenPathLabel,
  formatGradeForDoc,
  formatSafetyCheckLabel,
  isGradeSet,
} from "./restoration/types";

// Notes are mixed: AI markdown narrative + tag-prefixed log lines. Render
// markdown safely (no raw HTML pass-through) and let the helper below
// escape free text on every other branch.
const notesMd = markdownit({ html: false, linkify: true, typographer: true });

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * The toolkit appends to `conversation_notes` as a free-form log:
 * - Tagged lines: "Motivation: …", "Trigger/context: …", "Outcome: …"
 * - Bracketed action lines: "[Harmed peer check-in] yes"
 * - Optionally an AI documentation narrative prepended at the top with
 *   either the legacy `── Documentation narrative (AI) ──` separator or
 *   the new `## Documentation narrative (AI)` markdown header.
 *
 * Dumping that string into a single <p> collapses every newline and
 * shows raw markdown (#### What Happened) as literal text. This helper
 * renders the same data as a structured, readable HTML block — used by
 * both PDF builders so the downloaded packet matches the on-screen UI.
 */
const renderConversationNotesHtml = (
  rawNotes: string | null | undefined,
): string => {
  const text = (rawNotes ?? "").trim();
  if (!text) {
    return `<p style="color:#6b7280;font-style:italic;margin:0;">No notes yet.</p>`;
  }

  let narrativeMd: string | null = null;
  let remainder = text;
  const narrativeRe =
    /(?:^|\n)(?:──\s*Documentation narrative \(AI\)\s*──|##+\s*Documentation narrative \(AI\))\s*\n+([\s\S]*?)(?=\n[A-Z][\w\/ ()-]{1,40}:\s|\n\[[^\]]+\]|\n──|\n##+\s|$)/i;
  const narrativeMatch = remainder.match(narrativeRe);
  if (narrativeMatch && typeof narrativeMatch.index === "number") {
    narrativeMd = narrativeMatch[1].trim();
    remainder = (
      remainder.slice(0, narrativeMatch.index) +
      remainder.slice(narrativeMatch.index + narrativeMatch[0].length)
    ).trim();
  }

  type Tag = { label: string; value: string };
  const tagged: Tag[] = [];
  const actions: Tag[] = [];
  const proseLines: string[] = [];
  for (const rawLine of remainder.split(/\n/)) {
    const line = rawLine.trim();
    if (!line) {
      if (
        proseLines.length &&
        proseLines[proseLines.length - 1] !== ""
      ) {
        proseLines.push("");
      }
      continue;
    }
    const tagMatch = line.match(/^([A-Z][\w\/ ()-]{1,40}):\s*(.+)$/);
    if (tagMatch) {
      tagged.push({ label: tagMatch[1].trim(), value: tagMatch[2].trim() });
      continue;
    }
    const actionMatch = line.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (actionMatch) {
      actions.push({
        label: actionMatch[1].trim(),
        value: actionMatch[2].trim(),
      });
      continue;
    }
    proseLines.push(line);
  }

  const parts: string[] = [];

  if (narrativeMd) {
    parts.push(
      `<div style="margin:0 0 14px 0;padding:12px 16px;border:1px solid #e5e7eb;border-left:3px solid #ca8a04;border-radius:6px;background:#fffaf0;">` +
        `<div style="font-size:9pt;text-transform:uppercase;letter-spacing:0.06em;color:#92400e;margin-bottom:6px;font-weight:600;">AI documentation narrative</div>` +
        `<div style="font-size:10.5pt;line-height:1.6;color:#1a1a1a;">${notesMd.render(
          narrativeMd,
        )}</div>` +
        `</div>`,
    );
  }

  if (tagged.length) {
    parts.push(
      `<div style="margin:0 0 12px 0;">` +
        `<div style="font-size:9pt;text-transform:uppercase;letter-spacing:0.06em;color:#374151;margin-bottom:6px;font-weight:600;">Captured along the way</div>` +
        `<dl style="margin:0;font-size:10pt;line-height:1.55;">` +
        tagged
          .map(
            (t) =>
              `<div style="display:flex;gap:10px;margin:0 0 4px 0;page-break-inside:avoid;">` +
                `<dt style="min-width:160px;color:#374151;font-weight:600;">${escapeHtml(
                  t.label,
                )}</dt>` +
                `<dd style="margin:0;color:#1a1a1a;flex:1;">${escapeHtml(
                  t.value,
                )}</dd>` +
                `</div>`,
          )
          .join("") +
        `</dl></div>`,
    );
  }

  if (actions.length) {
    parts.push(
      `<div style="margin:0 0 12px 0;">` +
        `<div style="font-size:9pt;text-transform:uppercase;letter-spacing:0.06em;color:#374151;margin-bottom:6px;font-weight:600;">Action items</div>` +
        `<ul style="margin:0;padding-left:20px;font-size:10pt;line-height:1.55;">` +
        actions
          .map(
            (a) =>
              `<li style="margin-bottom:3px;"><strong>${escapeHtml(
                a.label,
              )}:</strong> ${escapeHtml(a.value || "—")}</li>`,
          )
          .join("") +
        `</ul></div>`,
    );
  }

  if (proseLines.length) {
    const paragraphs: string[] = [];
    let buf: string[] = [];
    const flush = () => {
      if (buf.length) {
        paragraphs.push(buf.join("<br>"));
        buf = [];
      }
    };
    for (const ln of proseLines) {
      if (ln === "") flush();
      else buf.push(escapeHtml(ln));
    }
    flush();
    if (paragraphs.length) {
      parts.push(
        paragraphs
          .map(
            (p) =>
              `<p style="margin:0 0 8px 0;font-size:10pt;line-height:1.55;">${p}</p>`,
          )
          .join(""),
      );
    }
  }

  return parts.join("");
};

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Build a short, scannable summary line for the active-incident header.
 *
 * Older versions of this page rendered the first 80 characters of
 * `incident_description` verbatim, which usually reduced the header to
 * the leader's first half-sentence and clipped mid-word. This helper
 * pulls together the first one or two real sentences, hard-caps the
 * length on a word boundary, and — if room remains — appends the
 * teacher's most-asked-for outcome so leaders can see the gist of the
 * referral at a glance without scrolling.
 */
const summarizeIncident = (incident: Incident): string => {
  const desc = (incident.incident_description ?? "").trim();
  if (!desc) {
    return "Add the student's name, grade, and what happened in the chat below.";
  }

  // Split into sentences while preserving terminal punctuation.
  const sentences = desc
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  let summary = sentences[0] || desc;
  if (summary.length < 90 && sentences.length > 1) {
    summary = `${summary} ${sentences[1]}`.trim();
  }

  const HARD_CAP = 200;
  if (summary.length > HARD_CAP) {
    summary = summary.slice(0, HARD_CAP).replace(/\s+\S*$/, "").trimEnd() + "…";
  }

  const teacherAsk = (incident.teacher_outcome_checked ?? [])[0]?.trim();
  if (teacherAsk && summary.length + teacherAsk.length + 18 < 260) {
    summary = `${summary} · Teacher asks: ${teacherAsk.toLowerCase()}`;
  }

  return summary;
};

// ── Constants ──────────────────────────────────────────────────────────────

const GRADE_GUIDANCE: Record<
  GradeLevel,
  { tone: string; openingMoves: string[]; keyQuestions: string[]; watchFor: string; avoid: string }
> = {
  "K-2": {
    tone: "Warm, slow, and concrete. Sit at eye level. Use simple feeling words. Match their energy down, not up.",
    openingMoves: [
      "Offer water or a fidget before saying anything.",
      "Say: 'I'm not mad at you. I just want to understand what happened.'",
      "Use a feelings chart if words aren't coming.",
    ],
    keyQuestions: [
      "'Can you show me what happened? You can draw it if you want.'",
      "'How did your body feel when that happened?'",
      "'Who do you think had a hard time because of what happened?'",
      "'What could we do to help them feel better?'",
    ],
    watchFor:
      "Shutting down, hiding their face, or nervous giggling — these are stress responses, not defiance. Slow down further.",
    avoid: "Don't ask 'why' — it triggers shame at this age. Ask 'what' and 'how' instead.",
  },
  "3-5": {
    tone: "Direct but warm. Fairness matters enormously to this age group. They need to feel heard before they can think.",
    openingMoves: [
      "'Something happened and I want to hear your side first.'",
      "'You seemed really frustrated earlier. I get it.'",
      "'Do you want to sit or stand while we talk?'",
    ],
    keyQuestions: [
      "'Walk me through what happened from your perspective.'",
      "'What were you feeling right before things went sideways?'",
      "'Who got affected by this, even if that wasn't your intention?'",
      "'What would make this feel more fair or right to you?'",
    ],
    watchFor:
      "Externalizing blame ('they started it') is developmentally normal — don't fight it. Reflect it back: 'Sounds like you felt provoked. And then what happened for you?'",
    avoid: "Long speeches. They tune out. Ask more than you tell.",
  },
  "6-8": {
    tone: "Respect is the currency. They are exquisitely sensitive to being talked down to. Stay curious, not clinical.",
    openingMoves: [
      "'Before we get into it — how are you feeling right now?'",
      "'I'm not here to corner you. I want to figure this out with you.'",
      "Give them a moment of silence — they often fill it themselves.",
    ],
    keyQuestions: [
      "'What was going on for you in that moment — not what you did, but what you were feeling?'",
      "'What did you need that you weren't getting?'",
      "'Who do you think was affected, and what do you think it was like for them?'",
      "'If you could rewind it, what — if anything — would you do differently?'",
    ],
    watchFor:
      "'I don't care' is often a cover for deep shame or fear. Gently name it: 'Sometimes people say they don't care when things actually matter a lot.'",
    avoid:
      "Comparing them to other students. Threatening consequences before repair is attempted. Anything that signals you've already decided the outcome.",
  },
  "9-12": {
    tone: "Treat them as capable of real accountability. Directness is respectful. Over-softening reads as condescending.",
    openingMoves: [
      "'Something happened that had real impact. I want to understand it fully before anything else.'",
      "'You're someone who can think through hard things. I want to do that together.'",
      "'Before we start — do you need anything?'",
    ],
    keyQuestions: [
      "'Tell me what happened from your perspective — start wherever makes sense.'",
      "'What were you trying to accomplish or protect in that moment?'",
      "'Who was affected, and what do you think the impact was — even if unintended?'",
      "'What does accountability look like to you here?'",
      "'What do you want the outcome of this conversation to be?'",
    ],
    watchFor:
      "Cynicism ('this doesn't matter') is often a history wound. Name it: 'It sounds like you've been through processes like this before that didn't go anywhere. I hear that.'",
    avoid: "Minimizing the situation. Rushing toward resolution before they feel heard. Premature closure destroys trust at this age.",
  },
};

const STUDENT_REFLECTION_PROMPTS: Record<
  GradeLevel,
  { intro: string; prompts: { question: string; hint: string }[]; closing: string }
> = {
  "K-2": {
    intro:
      "Something happened at school today. A grown-up wants to hear your side. You can write OR draw your answers — there's no wrong answer.",
    prompts: [
      { question: "What happened? (Draw it or write it.)", hint: "Just what you remember." },
      {
        question: "How did your body feel? Circle one:  😠 Mad   😢 Sad   😨 Scared   😕 Confused",
        hint: "There's no wrong feeling.",
      },
      {
        question: "Who else had a hard time because of what happened?",
        hint: "Think about other kids or your teacher.",
      },
      { question: "What is one thing you could do to help?", hint: "Even something small counts." },
      { question: "What do you need right now to feel okay?", hint: "It's okay to ask for help." },
    ],
    closing: "You did something brave by thinking about this. The adults here want to help you — not get you in trouble.",
  },
  "3-5": {
    intro:
      "Something happened, and the adults at school want to hear your side before anything else. This isn't about getting in trouble — it's about understanding what happened. Take your time.",
    prompts: [
      { question: "What happened, from your point of view?", hint: "Start from the beginning." },
      {
        question: "What were you feeling right before things went wrong?",
        hint: "Frustrated? Left out? Embarrassed? Angry? Try to name it.",
      },
      {
        question: "Who was affected by what happened, and how do you think they felt?",
        hint: "Think about other students, your teacher, anyone nearby.",
      },
      {
        question: "Is there anything you wish you had done differently?",
        hint: "No perfect answers — just honest ones.",
      },
      {
        question: "What do you think would help make things right?",
        hint: "What do YOU think should happen next?",
      },
      {
        question: "What do you need from the adults here?",
        hint: "We want to help, not just punish.",
      },
    ],
    closing:
      "Thank you for taking the time to think carefully. What you wrote here matters and will be part of what happens next.",
  },
  "6-8": {
    intro:
      "This form is yours. It's not a confession — it's your chance to share your perspective before any decisions are made. Be honest. The adults reading this are trying to understand, not just react.",
    prompts: [
      {
        question: "What happened? Walk through it from your point of view.",
        hint: "Start wherever makes sense. Details help.",
      },
      {
        question: "What were you feeling or thinking in the moments before things escalated?",
        hint: "Try to go beneath the surface — what was really going on for you?",
      },
      {
        question: "What were you trying to protect, get, or avoid in that moment?",
        hint: "Most behavior makes sense when you understand the need behind it.",
      },
      {
        question: "Who was affected by what happened, and what do you think it was like for them?",
        hint: "Even if you didn't mean to hurt anyone, impact matters.",
      },
      {
        question: "If you could rewind to that moment, what — if anything — would you change?",
        hint: "Even 'nothing' is an honest answer.",
      },
      {
        question: "What do you think needs to happen to repair the situation?",
        hint: "What would actually feel like a fair outcome to you?",
      },
      {
        question: "What do you need from the adults supporting you right now?",
        hint: "Be specific if you can.",
      },
    ],
    closing:
      "You took real time with this. That takes courage. Whatever comes next, the goal is to understand — not just to punish. Your perspective matters in that process.",
  },
  "9-12": {
    intro:
      "This is your space to share your perspective before any formal decisions are made. It is not a confession. It is not binding. It's an opportunity to be understood on your own terms. Take it seriously — your words here will shape the conversation that follows.",
    prompts: [
      {
        question: "Describe what happened from your perspective. Be as specific as you're comfortable being.",
        hint: "Include context from before the incident, not just during.",
      },
      {
        question: "What were you trying to accomplish, protect, or express in that moment?",
        hint: "Most behavior has a logic to it, even when it goes wrong. What was yours?",
      },
      {
        question:
          "Who was affected by what happened, and what do you think the impact was — even if unintended?",
        hint: "Honest about impact is not the same as accepting all blame.",
      },
      {
        question: "What role, if any, do you feel you played in how things escalated?",
        hint: "This is about honest reflection on your part — not taking blame for everything.",
      },
      {
        question: "What would accountability look like to you in this situation?",
        hint: "What do YOU think should happen — not what you think they want to hear.",
      },
      {
        question: "What do you need from the adults and systems around you to actually move forward?",
        hint: "This is a real question. If there's something that would help, say it.",
      },
      {
        question: "Is there anything else you want the people involved in this decision to know?",
        hint: "Anything not captured above.",
      },
    ],
    closing:
      "What you wrote here will be read by the adults supporting this process. The goal is repair and forward movement — not punishment for its own sake. Your honesty here is an act of self-advocacy.",
  },
};

const STUDENT_REFLECTION_REPEAT_INTRO: Record<GradeLevel, string> = {
  "K-2":
    "Sometimes this kind of thing happens more than once. Today you can tell us what feels the same, what feels different, and what you need to feel better.",
  "3-5":
    "This has happened more than once. The adults at school want to understand what's been going on and what needs to change—starting with your perspective.",
  "6-8":
    "This situation has happened before. Your job is to help us understand what triggers it and what support would help it be different next time.",
  "9-12":
    "This is a repeat incident. Take this seriously—your reflection helps us adjust supports and expectations so the outcome improves next time.",
};

const STUDENT_REFLECTION_REPEAT_CLOSING: Record<GradeLevel, string> = {
  "K-2": "Thank you for sharing. Adults will use your answers to help you make a new choice next time.",
  "3-5": "Thank you for taking time. Your answers help us build a plan that actually changes what happens next time.",
  "6-8": "Thanks for being honest. Your reflection will help adults adjust the plan so this doesn't keep repeating.",
  "9-12": "Your reflection will guide what adults change next time, so things move toward repair and forward progress.",
};

const STUDENT_REFLECTION_REPEAT_EXTRA_PROMPTS: Record<
  GradeLevel,
  { question: string; hint: string }[]
> = {
  "K-2": [
    { question: "Has this happened before? What feels the same today?", hint: "Point or draw what seems familiar." },
    { question: "What would help next time?", hint: "A grown-up can help you try a new way." },
  ],
  "3-5": [
    {
      question: "This has happened before. What feels the same today, and what feels different?",
      hint: "Think about what happened right before it started.",
    },
    {
      question: "What do you want adults to do differently next time?",
      hint: "Ask for what would make things more fair or feel safer.",
    },
  ],
  "6-8": [
    {
      question: "If we've talked about this before, what still needs to change so today can be different?",
      hint: "Name the trigger, the moment, or the pattern.",
    },
    {
      question: "What support from adults would help you repair and move forward next time?",
      hint: "Be specific if you can.",
    },
  ],
  "9-12": [
    {
      question: "This is a repeat. What was different about what led up to it this time?",
      hint: "What changed right before it happened?",
    },
    {
      question: "What accountability + support do you want adults to focus on next time?",
      hint: "What would feel like real progress to you?",
    },
  ],
};

// ── Helpers ────────────────────────────────────────────────────────────────
const createSessionId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (error) {
    console.error("Failed to generate UUID:", error);
  }
  return `rr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

// ── Build functions ────────────────────────────────────────────────────────
type DocContent = { html: string; text: string };

const buildConversationGuide = (incident: Incident): DocContent => {
  const g = GRADE_GUIDANCE[effectiveGrade(incident.grade_level)];
  const gradeLabel = formatGradeForDoc(incident.grade_level);
  const hasReflection = incident.student_reflection_responses.trim().length > 0;

  const priorChecked = incident.prior_interventions_checked ?? [];
  const priorFree = incident.prior_interventions?.trim();
  const priorAll = [...priorChecked, ...(priorFree ? [priorFree] : [])];

  const outcomeChecked = incident.teacher_outcome_checked ?? [];
  const outcomeFree = incident.teacher_requested_outcome?.trim();
  const outcomeAll = [...outcomeChecked, ...(outcomeFree ? [outcomeFree] : [])];

  const html = `
    <h3>Facilitator Conversation Guide</h3>
    <p><small>Grade ${gradeLabel} · ${new Date().toLocaleDateString()}</small></p>

    <h4>What Happened</h4>
    <p>${incident.incident_description || "No description provided."}</p>

    ${priorAll.length > 0 ? `
    <h4>Prior Interventions Already Tried</h4>
    <ul>${priorAll.map((i) => `<li>${i}</li>`).join("")}</ul>
    ` : ""}

    ${outcomeAll.length > 0 ? `
    <h4>What the Teacher Is Asking For</h4>
    <ul>${outcomeAll.map((o) => `<li>${o}</li>`).join("")}</ul>
    ` : ""}

    <h4>${hasReflection ? "What the Student Said" : "Student Reflection"}</h4>
    <p>${hasReflection
      ? incident.student_reflection_responses
      : "Student reflection not yet received."}</p>

    <h4>Your Tone & Posture</h4>
    <p>${g.tone}</p>

    <h4>How to Open</h4>
    <ul>${g.openingMoves.map((m) => `<li>${m}</li>`).join("")}</ul>

    <h4>Questions to Ask</h4>
    <ul>${g.keyQuestions.map((q) => `<li>${q}</li>`).join("")}</ul>

    <h4>Watch For</h4>
    <p>${g.watchFor}</p>

    <h4>Avoid</h4>
    <p>${g.avoid}</p>`;

  const text = [
    `FACILITATOR CONVERSATION GUIDE`,
    `Grade ${gradeLabel} · ${new Date().toLocaleDateString()}`,
    ``,
    `WHAT HAPPENED`,
    incident.incident_description || "No description provided.",
    ...(priorAll.length > 0
      ? [``, `PRIOR INTERVENTIONS ALREADY TRIED`, ...priorAll.map((i) => `→ ${i}`)]
      : []),
    ...(outcomeAll.length > 0
      ? [``, `WHAT THE TEACHER IS ASKING FOR`, ...outcomeAll.map((o) => `→ ${o}`)]
      : []),
    ``,
    hasReflection ? `WHAT THE STUDENT SAID` : `STUDENT REFLECTION`,
    hasReflection ? incident.student_reflection_responses : "Not yet received.",
    ``,
    `YOUR TONE & POSTURE`,
    g.tone,
    ``,
    `HOW TO OPEN`,
    ...g.openingMoves.map((m) => `→ ${m}`),
    ``,
    `QUESTIONS TO ASK`,
    ...g.keyQuestions.map((q) => `→ ${q}`),
    ``,
    `WATCH FOR`,
    g.watchFor,
    ``,
    `AVOID`,
    g.avoid,
  ].join("\n");

  return { html, text };
};

const downloadIncidentPacketPDF = (incident: Incident) => {
  const buildDecisionRationale = (inc: Incident): DocContent => {
    const safetyLabel = formatSafetyCheckLabel(inc.safety_check);
    const pathLabel = formatChosenPathLabel(inc.chosen_path);
    const html = `<h3>Decision Rationale</h3>
<p><small>${new Date().toLocaleDateString()}</small></p>
<table><tr><td><b>Student:</b> ${inc.student_identifier || "Not specified"}</td><td><b>Grade:</b> ${formatGradeForDoc(inc.grade_level)}</td><td><b>Teacher:</b> ${inc.teacher_name || "Not specified"}</td></tr></table>
<h4>What Happened</h4>
<p>${inc.incident_description || "Not provided."}</p>
<h4>Safety Assessment</h4>
<p>${safetyLabel.html}</p>
<h4>Chosen Path</h4>
<p>${pathLabel}</p>
<h4>Rationale</h4>
<p>${inc.chosen_path
      ? "Based on the incident description and student reflection, the chosen path best protects the student's dignity, the relationship, and community safety while allowing repair to be co-created."
      : "No path has been chosen yet — return to the Conversation step to select one."}</p>`;
    const text = [`DECISION RATIONALE`, `${new Date().toLocaleDateString()}`, ``, `Student: ${inc.student_identifier || "Not specified"}`, `Grade: ${formatGradeForDoc(inc.grade_level)}`, `Teacher: ${inc.teacher_name || "Not specified"}`, ``, `WHAT HAPPENED`, inc.incident_description || "Not provided.", ``, `SAFETY ASSESSMENT`, safetyLabel.text, ``, `CHOSEN PATH`, pathLabel, ``, `RATIONALE`, inc.chosen_path ? "Chosen path protects dignity, relationship, and safety while allowing repair." : "No path chosen yet."].join("\n");
    return { html, text };
  };

  const buildRepairAgreement = (inc: Incident): DocContent => {
    if (inc.safety_check === "pause_needed") {
      const html = `<h3>Pause Plan</h3><p>A pause is recommended before any repair conversation.</p><h4>Immediate Steps</h4><ul><li>Ensure the student has a calm, supervised space.</li><li>Identify a support person the student trusts.</li><li>Set a time to re-assess readiness — not to discipline, but to check in.</li></ul><h4>Next Step</h4><p>When readiness cues return, move to the conversation guide.</p><p><b>Follow-up date:</b> ${inc.follow_up_date || "Not yet set."}</p>`;
      const text = [`PAUSE PLAN`, ``, `Immediate Steps:`, `→ Calm, supervised space`, `→ Identify support person`, `→ Re-assess readiness`, ``, `Follow-up: ${inc.follow_up_date || "Not yet set."}`].join("\n");
      return { html, text };
    }
    const html = `<h3>Draft Repair Agreement</h3><p><i>To be co-created — not imposed. This is a starting point.</i></p><h4>What Happened</h4><p>${inc.incident_description || "Add a neutral description."}</p><h4>What We Are Repairing</h4><p><i>Describe the impact in plain, non-blaming language.</i></p><h4>Steps We Are Agreeing To</h4><p><i>(Co-create with the student — do not fill this in before the conversation.)</i></p><h4>Adult Commitments</h4><p><i>(What will the adults do to support follow-through?)</i></p><p><b>Follow-up check-in:</b> ${inc.follow_up_date || "To be set."}</p>`;
    const text = [`DRAFT REPAIR AGREEMENT`, `(Co-created, not imposed)`, ``, `WHAT HAPPENED`, inc.incident_description || "[Add description]", ``, `WHAT WE ARE REPAIRING`, `[Describe impact]`, ``, `STEPS AGREED TO`, `[Co-create with student]`, ``, `ADULT COMMITMENTS`, `[Support actions]`, ``, `Follow-up: ${inc.follow_up_date || "To be set."}`].join("\n");
    return { html, text };
  };

  const buildSupportPlan = (inc: Incident): DocContent => {
    const html = `<h3>Internal Support Plan</h3><p><small>${new Date().toLocaleDateString()}</small></p><table><tr><td><b>Student:</b> ${inc.student_identifier || "Not specified"}</td><td><b>Grade:</b> ${formatGradeForDoc(inc.grade_level)}</td></tr></table><h4>Conversation Notes</h4>${renderConversationNotesHtml(inc.conversation_notes)}<h4>Adult Actions</h4><ul><li>Check in with student within 24 hours of re-entry.</li><li>Brief the classroom teacher on re-entry tone.</li><li>Monitor for follow-up needs at ${inc.follow_up_date || "TBD"}.</li></ul><h4>Parent Call Notes</h4><p>${inc.parent_call_notes || "No parent call notes yet."}</p>`;
    const text = [`INTERNAL SUPPORT PLAN`, `${new Date().toLocaleDateString()}`, ``, `Student: ${inc.student_identifier || "Not specified"}`, `Grade: ${formatGradeForDoc(inc.grade_level)}`, ``, `CONVERSATION NOTES`, inc.conversation_notes || "No notes yet.", ``, `ADULT ACTIONS`, `→ Check in within 24 hours`, `→ Brief classroom teacher`, `→ Monitor at ${inc.follow_up_date || "TBD"}`, ``, `PARENT CALL NOTES`, inc.parent_call_notes || "No parent call notes yet."].join("\n");
    return { html, text };
  };

  const buildCompletionRecord = (inc: Incident): DocContent => {
    const removalLabel = inc.removal_type === "administrative_redirect"
      ? "Office Redirect (≤ one period)"
      : inc.removal_type === "formal_removal"
        ? "Formal Removal — formal documentation required"
        : "Not classified.";
    const priorChecked = inc.prior_interventions_checked ?? [];
    const priorFree = inc.prior_interventions?.trim();
    const priorAll = [...priorChecked, ...(priorFree ? [priorFree] : [])];
    const outcomeChecked = inc.teacher_outcome_checked ?? [];
    const outcomeFree = inc.teacher_requested_outcome?.trim();
    const outcomeAll = [...outcomeChecked, ...(outcomeFree ? [outcomeFree] : [])];
    const dueProcessHtml = inc.due_process_acknowledged
      ? `✓ Student given opportunity to respond — ${inc.due_process_timestamp}${inc.due_process_admin_initials ? ` · Admin: ${inc.due_process_admin_initials}` : ""}${inc.due_process_student_name ? ` · Student: ${inc.due_process_student_name}` : ""}`
      : "⚠ Due process acknowledgment not recorded.";
    const witnessHtml = (inc.witness_accounts?.length > 0)
      ? `<h4>Additional Accounts (${inc.witness_accounts.length})</h4><ul>${inc.witness_accounts.map(w => `<li><strong>${w.witness_name || "Unnamed"}</strong> (${w.witness_role || "role not specified"}): ${w.account || "No account recorded."}</li>`).join("")}</ul>`
      : "";
    const completionPathLabel = formatChosenPathLabel(inc.chosen_path);
    const html = `<h3>Completion Record</h3><p><small>Generated: ${new Date().toLocaleDateString()}</small></p><table><tr><td><b>Student:</b> ${inc.student_identifier || "Not specified"}</td><td><b>Grade:</b> ${formatGradeForDoc(inc.grade_level)}</td><td><b>Teacher:</b> ${inc.teacher_name || "Not specified"}</td></tr></table><h4>Removal Type</h4><p>${removalLabel}</p><h4>Rationale for Removal Length</h4><p>${inc.removal_rationale || "Not documented."}</p><h4>Referring Behavior</h4><p>${inc.incident_description || "Not provided."}</p>${priorAll.length > 0 ? `<h4>Prior Interventions</h4><ul>${priorAll.map(i => `<li>${i}</li>`).join("")}</ul>` : ""}${outcomeAll.length > 0 ? `<h4>Teacher's Requested Outcome</h4><ul>${outcomeAll.map(o => `<li>${o}</li>`).join("")}</ul>` : ""}<h4>Due Process</h4><p>${dueProcessHtml}</p>${witnessHtml}<h4>Path Taken</h4><p>${completionPathLabel}</p><h4>Status</h4><ul><li>Student reflection received: ${inc.reflection_received ? "Yes" : "No"}</li><li>Conversation completed: ${inc.conversation_completed ? "Yes" : "No"}</li><li>In-School reflection completed: ${inc.iss_reflection_completed ? "Yes" : "No"}</li></ul><h4>ISS Academic Work Plan</h4><p>${inc.iss_work_plan || "Not documented."}</p><h4>Return to Class Plan</h4><p>${inc.return_to_class_plan || "Not documented."}${inc.reentry_conversation_needed ? `<br/>Re-entry conversation needed · Facilitator: ${inc.reentry_facilitator || "TBD"}` : ""}${inc.reentry_checkin_schedule ? `<br/>Check-in schedule: ${inc.reentry_checkin_schedule}` : ""}</p><p><b>Follow-up date:</b> ${inc.follow_up_date || "Not set."}</p>`;
    const text = [`COMPLETION RECORD`, `Generated: ${new Date().toLocaleDateString()}`, ``, `Student: ${inc.student_identifier || "Not specified"}`, `Grade: ${formatGradeForDoc(inc.grade_level)}`, `Teacher: ${inc.teacher_name || "Not specified"}`, ``, `REMOVAL TYPE: ${removalLabel}`, inc.removal_rationale ? `Rationale: ${inc.removal_rationale}` : "", ``, `REFERRING BEHAVIOR`, inc.incident_description || "Not provided.", ...(priorAll.length > 0 ? [``, `PRIOR INTERVENTIONS`, ...priorAll.map(i => `→ ${i}`)] : []), ...(outcomeAll.length > 0 ? [``, `TEACHER'S REQUESTED OUTCOME`, ...outcomeAll.map(o => `→ ${o}`)] : []), ``, `DUE PROCESS`, inc.due_process_acknowledged ? `✓ Student given opportunity to respond — ${inc.due_process_timestamp}${inc.due_process_admin_initials ? ` · Admin: ${inc.due_process_admin_initials}` : ""}${inc.due_process_student_name ? ` · Student: ${inc.due_process_student_name}` : ""}` : `⚠ Due process acknowledgment not recorded.`, ...(inc.witness_accounts?.length > 0 ? [``, `ADDITIONAL ACCOUNTS (${inc.witness_accounts.length})`, ...inc.witness_accounts.map((w, i) => `  ${i + 1}. ${w.witness_name || "Unnamed"} (${w.witness_role || "role not specified"}): ${w.account || "No account recorded."}`)] : []), ``, `Path: ${completionPathLabel}`, ``, `STATUS`, `Reflection: ${inc.reflection_received ? "Yes" : "No"}`, `Conversation: ${inc.conversation_completed ? "Yes" : "No"}`, `ISS reflection: ${inc.iss_reflection_completed ? "Yes" : "No"}`, ``, `ISS ACADEMIC WORK PLAN`, inc.iss_work_plan || "Not documented.", ``, `RETURN TO CLASS PLAN`, inc.return_to_class_plan || "Not documented.", inc.reentry_conversation_needed ? `Re-entry conversation needed · Facilitator: ${inc.reentry_facilitator || "TBD"}` : "", inc.reentry_checkin_schedule ? `Check-in schedule: ${inc.reentry_checkin_schedule}` : "", ``, `Follow-up: ${inc.follow_up_date || "Not set."}`].filter(Boolean).join("\n");
    return { html, text };
  };

  const sections = [
    { title: "Facilitator Conversation Guide", doc: buildConversationGuide(incident) },
    { title: "Decision Rationale", doc: buildDecisionRationale(incident) },
    { title: "Repair Agreement", doc: buildRepairAgreement(incident) },
    { title: "Support Plan", doc: buildSupportPlan(incident) },
    { title: "Completion Record", doc: buildCompletionRecord(incident) },
  ];

  const html = `
    <div style="font-family:Georgia,serif;padding:40px;max-width:700px;margin:0 auto;">
      <h1 style="color:#0D4436;border-bottom:2px solid #ca8a04;padding-bottom:10px;margin-bottom:8px;">Behavior Incident Toolkit — Incident Packet</h1>
      <p style="color:#6b7280;font-size:10pt;margin-bottom:32px;">Generated ${new Date().toLocaleDateString()} · Student: ${incident.student_identifier || "Not specified"} · Grade ${formatGradeForDoc(incident.grade_level)}</p>
      ${sections.map((s) => `
        <div style="margin-bottom:32px;page-break-inside:avoid;">
          <h2 style="font-size:14pt;color:#0D4436;margin-bottom:8px;">${s.title}</h2>
          <div style="font-size:10.5pt;line-height:1.7;color:#1a1a1a;">${s.doc.html}</div>
        </div>`).join("")}
    </div>`;

  const element = document.createElement("div");
  element.innerHTML = html;
  html2pdf()
    .set({
      margin: 0.5,
      filename: `incident-packet-${new Date().toISOString().split("T")[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    })
    .from(element)
    .save();
};

// Same packet, returned as a Blob for snapshot saving rather than
// triggered as a download. We rebuild the HTML via the existing
// `downloadIncidentPacketPDF` function's logic by inlining the same
// section helpers — kept identical so the saved PDF matches what the
// user sees when they hit Download.
const buildIncidentPacketBlob = async (
  incident: Incident,
  buildConversationGuide: (incident: Incident) => DocContent,
): Promise<{ blob: Blob; filename: string }> => {
  const buildDecisionRationale = (inc: Incident): DocContent => {
    const safetyLabel = formatSafetyCheckLabel(inc.safety_check);
    const pathLabel = formatChosenPathLabel(inc.chosen_path);
    const html = `<h3>Decision Rationale</h3>
<p><small>${new Date().toLocaleDateString()}</small></p>
<table><tr><td><b>Student:</b> ${inc.student_identifier || "Not specified"}</td><td><b>Grade:</b> ${formatGradeForDoc(inc.grade_level)}</td><td><b>Teacher:</b> ${inc.teacher_name || "Not specified"}</td></tr></table>
<h4>What Happened</h4>
<p>${inc.incident_description || "Not provided."}</p>
<h4>Safety Assessment</h4>
<p>${safetyLabel.html}</p>
<h4>Chosen Path</h4>
<p>${pathLabel}</p>
<h4>Rationale</h4>
<p>${inc.chosen_path
      ? "Based on the incident description and student reflection, the chosen path best protects the student's dignity, the relationship, and community safety while allowing repair to be co-created."
      : "No path has been chosen yet — return to the Conversation step to select one."}</p>`;
    return { html, text: "" };
  };
  const buildRepairAgreement = (inc: Incident): DocContent => {
    if (inc.safety_check === "pause_needed") {
      return {
        html: `<h3>Pause Plan</h3><p>A pause is recommended before any repair conversation.</p><h4>Immediate Steps</h4><ul><li>Ensure the student has a calm, supervised space.</li><li>Identify a support person the student trusts.</li><li>Set a time to re-assess readiness — not to discipline, but to check in.</li></ul><p><b>Follow-up date:</b> ${inc.follow_up_date || "Not yet set."}</p>`,
        text: "",
      };
    }
    return {
      html: `<h3>Draft Repair Agreement</h3><p><i>To be co-created — not imposed. This is a starting point.</i></p><h4>What Happened</h4><p>${inc.incident_description || "Add a neutral description."}</p><h4>What We Are Repairing</h4><p><i>Describe the impact in plain, non-blaming language.</i></p><h4>Steps We Are Agreeing To</h4><p><i>(Co-create with the student — do not fill this in before the conversation.)</i></p><h4>Adult Commitments</h4><p><i>(What will the adults do to support follow-through?)</i></p><p><b>Follow-up check-in:</b> ${inc.follow_up_date || "To be set."}</p>`,
      text: "",
    };
  };
  const buildSupportPlan = (inc: Incident): DocContent => ({
    html: `<h3>Internal Support Plan</h3><p><small>${new Date().toLocaleDateString()}</small></p><table><tr><td><b>Student:</b> ${inc.student_identifier || "Not specified"}</td><td><b>Grade:</b> ${formatGradeForDoc(inc.grade_level)}</td></tr></table><h4>Conversation Notes</h4>${renderConversationNotesHtml(inc.conversation_notes)}<h4>Adult Actions</h4><ul><li>Check in with student within 24 hours of re-entry.</li><li>Brief the classroom teacher on re-entry tone.</li><li>Monitor for follow-up needs at ${inc.follow_up_date || "TBD"}.</li></ul><h4>Parent Call Notes</h4><p>${inc.parent_call_notes || "No parent call notes yet."}</p>`,
    text: "",
  });

  const sections = [
    { title: "Facilitator Conversation Guide", doc: buildConversationGuide(incident) },
    { title: "Decision Rationale", doc: buildDecisionRationale(incident) },
    { title: "Repair Agreement", doc: buildRepairAgreement(incident) },
    { title: "Support Plan", doc: buildSupportPlan(incident) },
  ];

  const html = `
    <div style="font-family:Georgia,serif;padding:40px;max-width:700px;margin:0 auto;">
      <h1 style="color:#0D4436;border-bottom:2px solid #ca8a04;padding-bottom:10px;margin-bottom:8px;">Behavior Incident Toolkit — Incident Packet</h1>
      <p style="color:#6b7280;font-size:10pt;margin-bottom:32px;">Generated ${new Date().toLocaleDateString()} · Student: ${incident.student_identifier || "Not specified"} · Grade ${formatGradeForDoc(incident.grade_level)}</p>
      ${sections
        .map(
          (s) => `
        <div style="margin-bottom:32px;page-break-inside:avoid;">
          <h2 style="font-size:14pt;color:#0D4436;margin-bottom:8px;">${s.title}</h2>
          <div style="font-size:10.5pt;line-height:1.7;color:#1a1a1a;">${s.doc.html}</div>
        </div>`,
        )
        .join("")}
    </div>`;

  const element = document.createElement("div");
  element.innerHTML = html;

  const dateStr = new Date().toISOString().split("T")[0];
  const slug = (incident.student_identifier || "student")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase();
  const filename = `incident-packet-${slug}-${dateStr}.pdf`;

  const blob: Blob = await html2pdf()
    .set({
      margin: 0.5,
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    })
    .from(element)
    .outputPdf("blob");

  return { blob, filename };
};

// ── Error boundary ─────────────────────────────────────────────────────────
class RestorationRoomErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("Restoration Room error:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Page.Container>
          <Page.Header className="text-left">
            <h1 className="text-2xl md:text-3xl font-semibold text-white font-literata">Behavior Incident Toolkit</h1>
            <p className="text-white/80 mt-2">Document, respond to, and resolve student behavior incidents — from office referral to re-entry.</p>
          </Page.Header>
          <Page.Content className="flex items-center justify-center min-h-[50vh]">
            <ContentCard className="max-w-md w-full text-center">
              <p className="text-sm text-gray-700 mb-3">We hit a loading issue. Please refresh and try again.</p>
              <Button onClick={() => window.location.reload()} className="bg-bodhi-teal text-white">
                Reload
              </Button>
            </ContentCard>
          </Page.Content>
        </Page.Container>
      );
    }
    return this.props.children;
  }
}

// ── Save state type ────────────────────────────────────────────────────────
type SaveState = "idle" | "saving" | "saved" | "error";

// ── Main component ────────────────────────────────────────────────────────
export const RestorationRoomPage = () => {
  const { session, loading: authLoading } = useAuth();
  const [districtConfig, setDistrictConfig] = useDistrictConfig(session?.user?.id);

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [activeIncidentId, setActiveIncidentId] = useState<string | null>(null);
  const [activeStage, setActiveStage] = useState<ChatTabStage>("incident");
  const chatHandleRef = useRef<RestorationRoomChatHandle>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | "all">("all");
  const [saveState, setSaveState] = useState<SaveState>("idle");

  // Persistent Documents drawer for the active incident. Mirrors the
  // chat's attachments list so the drawer can render the same data
  // outside chat scroll. The chat is the source of truth for upload/
  // remove side-effects; the page just holds a snapshot for rendering.
  const [docsOpen, setDocsOpen] = useState(false);
  const [activeAttachments, setActiveAttachments] = useState<FileUpload[]>([]);
  const [incidentSnapshots, setIncidentSnapshots] = useState<StorageUserFile[]>(
    [],
  );
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [savingSnapshotKind, setSavingSnapshotKind] = useState<string | null>(
    null,
  );

  // Reset drawer state whenever the active incident changes so a stale
  // list never leaks across incidents. Snapshots are then refreshed
  // from the server (RLS filters to own + school-shared rows).
  useEffect(() => {
    setDocsOpen(false);
    setActiveAttachments([]);
    setIncidentSnapshots([]);
    if (!activeIncidentId) {
      setSnapshotsLoading(false);
      return;
    }
    let cancelled = false;
    setSnapshotsLoading(true);
    (async () => {
      try {
        const snaps = await storageService.getSnapshotsByContext(
          "restoration_incident",
          activeIncidentId,
        );
        if (!cancelled) setIncidentSnapshots(snaps);
      } finally {
        if (!cancelled) setSnapshotsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeIncidentId]);

  // AE-58: School view state
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [schoolIncidents, setSchoolIncidents] = useState<Incident[]>([]);
  const [viewMode, setViewMode] = useState<"mine" | "school" | "insights">("mine");

  // Refs for debounce and pending-fields buffer
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFields = useRef<Partial<Incident>>({});
  const activeIncidentIdRef = useRef<string | null>(null);

  // Draft-incident tracking. A "draft" is an in-memory incident the
  // leader has just opened but hasn't yet given enough identifying
  // detail for us to persist. We only call incidentService.create()
  // once the threshold (student name + incident description) is met,
  // which prevents orphaned rows when someone explores and backs out.
  // Tracked in a ref Set so that lookups during typing don't re-render.
  const draftIdsRef = useRef<Set<string>>(new Set());
  // Set while a promotion (draft → DB row) is in flight, so concurrent
  // updateIncident() calls don't fire a second create.
  const promotingRef = useRef(false);

  // Keep ref in sync with state so flush callbacks can read the current value
  useEffect(() => {
    activeIncidentIdRef.current = activeIncidentId;
  }, [activeIncidentId]);

  const activeIncident =
    incidents.find((i) => i.id === activeIncidentId) ??
    schoolIncidents.find((i) => i.id === activeIncidentId) ??
    null;

  // Derived: true when viewing a colleague's incident (read-only)
  const isReadOnly = !!activeIncident && !!currentUserId && activeIncident.user_id !== currentUserId;

  // Flush any pending debounced write immediately. Drafts are skipped
  // because they don't have a DB row yet — those edits stay in local
  // state and either promote later (once threshold is met) or are
  // discarded when the leader backs out.
  const flushPending = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const id = activeIncidentIdRef.current;
    if (!id || draftIdsRef.current.has(id)) return;
    const fields = pendingFields.current;
    if (Object.keys(fields).length > 0) {
      pendingFields.current = {};
      setSaveState("saving");
      incidentService.update(id, fields)
        .then(() => setSaveState("saved"))
        .catch(() => {
          setSaveState("error");
          toast.error("Save failed — your changes are cached locally");
        });
    }
  };

  // Drop the active draft if any. Called when the leader backs out of
  // the chat without entering enough detail — no DB row was ever
  // created so there's nothing to clean up server-side.
  const discardActiveDraft = () => {
    const id = activeIncidentIdRef.current;
    if (!id || !draftIdsRef.current.has(id)) return;
    draftIdsRef.current.delete(id);
    pendingFields.current = {};
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    promotingRef.current = false;
    setIncidents((prev) => prev.filter((inc) => inc.id !== id));
    setSaveState("idle");
  };

  // Bootstrap: load incidents for the signed-in user only. Re-runs when the
  // account changes so one leader never inherits another's in-memory rows.
  useEffect(() => {
    let cancelled = false;

    if (!currentUserId) {
      if (authLoading) return;
      setIncidents([]);
      setSchoolIncidents([]);
      setActiveIncidentId(null);
      draftIdsRef.current.clear();
      setIsBootstrapping(false);
      return;
    }

    setIsBootstrapping(true);
    incidentService
      .listMine()
      .then((rows) => {
        if (cancelled) return;
        setIncidents((prev) => {
          const drafts = prev.filter(
            (i) => draftIdsRef.current.has(i.id) && i.user_id === currentUserId,
          );
          return [...drafts, ...rows];
        });
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load incidents — working from local cache");
      })
      .finally(() => {
        if (!cancelled) setIsBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId, authLoading]);

  // Resolve school id whenever the signed-in account changes.
  useEffect(() => {
    const userId = session?.user?.id ?? null;
    setCurrentUserId(userId);

    if (!userId) {
      setSchoolId(null);
      return;
    }

    let cancelled = false;
    supabase
      .from("profiles")
      .select("school_id")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setSchoolId(data?.school_id ?? null);
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  // AE-58: when viewMode switches to school or insights, fetch colleagues' incidents
  useEffect(() => {
    if ((viewMode !== "school" && viewMode !== "insights") || !schoolId) return;
    let cancelled = false;
    incidentService.listSchool(schoolId)
      .then(rows => { if (!cancelled) setSchoolIncidents(rows); })
      .catch(() => { if (!cancelled) toast.error("Could not load school incidents"); });
    return () => { cancelled = true; };
  }, [viewMode, schoolId]);

  // Flush pending writes on unmount
  useEffect(() => {
    return () => { flushPending(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateIncident = (fields: Partial<Incident>) => {
    const id = activeIncidentIdRef.current;
    if (!id) return;

    // AE-58: block writes when viewing a colleague's incident
    if (isReadOnly) return;

    // Optimistic update — applied for both drafts and persisted rows so
    // the chat sees the latest values regardless of save state. We
    // capture the merged record from inside the updater so that two
    // updateIncident() calls fired in the same render cycle each see
    // the cumulative state instead of a stale snapshot.
    let merged: Incident | undefined;
    setIncidents((prev) =>
      prev.map((inc) => {
        if (inc.id !== id) return inc;
        merged = { ...inc, ...fields, updated_at: new Date().toISOString() };
        return merged;
      })
    );

    // ── Draft path ───────────────────────────────────────────────────────
    // While the row is still a draft, do NOT call the DB. Wait until the
    // leader has entered enough identifying detail (student name +
    // incident description). Once the threshold trips, promote the
    // draft to a real row by calling create() with the same id, so the
    // chat session does not reset.
    if (draftIdsRef.current.has(id)) {
      const hasName = !!merged?.student_identifier?.trim();
      const hasDesc = !!merged?.incident_description?.trim();
      // We also wait until the leader has captured a real grade
      // before promoting the draft to the DB. Without this, a draft
      // with grade_level === "" could be sent to Supabase and either
      // (a) fail the CHECK constraint pre-migration, or (b) persist
      // an "unset" record we can't easily distinguish from a real
      // 6-8 case post-migration.
      const hasGrade = isGradeSet(merged?.grade_level);

      // Buffer fields in case promotion is in flight or kicks off below;
      // any fields the user types during the create RPC are flushed to
      // the real row on success.
      pendingFields.current = { ...pendingFields.current, ...fields };

      if (!hasName || !hasDesc || !hasGrade || !merged) {
        // Still a draft — pure in-memory edit, nothing else to do.
        setSaveState("idle");
        return;
      }
      if (promotingRef.current) return;

      promotingRef.current = true;
      setSaveState("saving");

      // Strip server-managed fields from the create payload but keep
      // the locally-generated id so it stays stable post-promotion.
      const { created_at: _ca, updated_at: _ua, user_id: _uid, creator_name: _cn, ...payload } = merged;
      void _ca; void _ua; void _uid; void _cn;

      incidentService
        .create(payload)
        .then((real) => {
          draftIdsRef.current.delete(id);
          // Merge server fields in (created_at/updated_at/user_id/etc.)
          // while preserving any keystrokes that happened during the RPC.
          setIncidents((prev) =>
            prev.map((inc) => (inc.id === id ? { ...inc, ...real } : inc))
          );

          // Anything typed while the create was in flight needs a
          // follow-up update against the now-real row.
          const buffered = pendingFields.current;
          pendingFields.current = {};
          promotingRef.current = false;
          if (Object.keys(buffered).length > 0) {
            setSaveState("saving");
            incidentService
              .update(id, buffered)
              .then(() => setSaveState("saved"))
              .catch(() => {
                setSaveState("error");
                toast.error("Save failed — your changes are cached locally");
              });
          } else {
            setSaveState("saved");
          }
        })
        .catch((err) => {
          console.error("[RestorationRoomPage] draft promotion failed:", err);
          promotingRef.current = false;
          setSaveState("error");
          toast.error("Could not save this incident — please try again");
          // Leave it in the draft set so the next edit retries the create.
        });
      return;
    }

    // ── Persisted path ───────────────────────────────────────────────────
    pendingFields.current = { ...pendingFields.current, ...fields };

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSaveState("saving");
    debounceTimer.current = setTimeout(() => {
      const currentId = activeIncidentIdRef.current;
      const toSave = pendingFields.current;
      if (!currentId || Object.keys(toSave).length === 0) return;
      // A late-firing debounce after the user has navigated to a
      // different incident (or back to the dashboard) shouldn't write.
      if (draftIdsRef.current.has(currentId)) return;
      pendingFields.current = {};
      debounceTimer.current = null;
      incidentService.update(currentId, toSave)
        .then(() => setSaveState("saved"))
        .catch(() => {
          setSaveState("error");
          toast.error("Save failed — your changes are cached locally");
        });
    }, 1000);
  };

  const handleNewIncident = async () => {
    // We deliberately don't call incidentService.create() here. A leader
    // who clicks "+ New Incident" then closes the tab or backs out
    // before naming the student should not leave an orphaned row in
    // the database. Instead we mint a UUID locally, build an in-memory
    // draft, and only persist once the threshold (student name +
    // incident description) is met inside updateIncident().
    //
    // Only one draft at a time is allowed — clicking "+ New Incident"
    // again while a draft is already active reuses the existing draft.
    const existingDraftId = Array.from(draftIdsRef.current)[0];
    if (existingDraftId) {
      setActiveIncidentId(existingDraftId);
      setActiveStage("incident");
      setSaveState("idle");
      return;
    }

    const newId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        // Fallback for older browsers — unlikely in practice but keeps
        // this from throwing in test environments.
        : `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();
    const draft: Incident = {
      ...DEFAULT_INCIDENT,
      id: newId,
      created_at: now,
      updated_at: now,
      user_id: currentUserId,
    };
    draftIdsRef.current.add(newId);
    setIncidents((prev) => [draft, ...prev]);
    setActiveIncidentId(newId);
    setActiveStage("incident");
    setSaveState("idle");
  };

  const handleResetAll = async () => {
    // Drafts have no DB row — only delete the persisted ones.
    const persistedIds = incidents
      .map((i) => i.id)
      .filter((id) => !draftIdsRef.current.has(id));
    try {
      await Promise.all(persistedIds.map((id) => incidentService.delete(id)));
    } catch {
      toast.error("Some incidents could not be deleted — please try again");
    }
    draftIdsRef.current.clear();
    setIncidents([]);
    setActiveIncidentId(null);
    toast.success("All incidents cleared.");
  };

  const handleDeleteIncident = async (id: string) => {
    // Drafts can be removed without a server round-trip.
    if (draftIdsRef.current.has(id)) {
      draftIdsRef.current.delete(id);
      setIncidents((prev) => prev.filter((inc) => inc.id !== id));
      if (activeIncidentId === id) setActiveIncidentId(null);
      return;
    }
    try {
      await incidentService.delete(id);
      setIncidents((prev) => prev.filter((inc) => inc.id !== id));
      if (activeIncidentId === id) setActiveIncidentId(null);
      toast.success("Incident removed.");
    } catch {
      toast.error("Could not delete incident — please try again");
    }
  };

  // Inline status change from the dashboard list. Lets the admin move an
  // incident to Closed / Follow-up / etc. without having to open it first.
  const handleIncidentStatusChange = async (
    id: string,
    nextStatus: IncidentStatus,
  ) => {
    const updates: Partial<Incident> = { status: nextStatus };
    // Mirror the "Mark complete" behavior so the record is fully closed
    // everywhere (filters, counts, doc panel) when closed from the list.
    if (nextStatus === "closed") updates.iss_reflection_completed = true;

    setIncidents((prev) =>
      prev.map((inc) =>
        inc.id === id
          ? { ...inc, ...updates, updated_at: new Date().toISOString() }
          : inc,
      ),
    );

    try {
      await incidentService.update(id, updates);
      const labels: Record<IncidentStatus, string> = {
        intake: "Intake",
        coaching: "Coaching",
        followup: "Follow-up",
        closed: "Closed",
        paused: "Paused",
      };
      toast.success(`Status updated to ${labels[nextStatus]}`);
    } catch {
      toast.error("Could not update status — please try again");
    }
  };

  const handleCloseIncident = async () => {
    const id = activeIncidentIdRef.current;
    if (!id) return;
    // A draft has no DB row to close — discard it instead.
    if (draftIdsRef.current.has(id)) {
      discardActiveDraft();
      setActiveIncidentId(null);
      return;
    }
    // Flush any pending debounced write before closing
    flushPending();
    try {
      await incidentService.update(id, { status: "closed", iss_reflection_completed: true });
      setIncidents((prev) =>
        prev.map((inc) =>
          inc.id === id
            ? { ...inc, status: "closed", iss_reflection_completed: true, updated_at: new Date().toISOString() }
            : inc
        )
      );
      toast.success("Incident marked as complete.");
    } catch {
      toast.error("Could not close incident — please try again");
    }
  };

  // Flush pending write when switching active incident
  const handleSelectIncidentWithFlush = (id: string) => {
    flushPending();
    setSaveState("idle");
    pendingFields.current = {};
    const inc = incidents.find((i) => i.id === id);
    if (!inc) return;
    setActiveIncidentId(id);
    setActiveStage("incident");
  };

  // AE-58: select a school (colleague's) incident — navigate to it read-only
  const handleSelectSchoolIncident = (id: string) => {
    flushPending();
    setSaveState("idle");
    pendingFields.current = {};
    const inc = schoolIncidents.find((i) => i.id === id);
    if (!inc) return;
    setActiveIncidentId(id);
    setActiveStage("incident");
  };

  const handleSaveToLibrary = async (title: string, content: string) => {
    if (!session?.user?.id) {
      toast.error("Sign in to save to library.");
      return;
    }
    setIsSaving(true);
    try {
      const { error } = await supabase.from("user_resources").insert({
        user_id: session.user.id,
        title,
        resource_type: "journal",
        content,
        ai_summary: "Generated in the Behavior Incident Toolkit",
      });
      if (error) throw error;
      toast.success("Saved to library.");
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isBootstrapping) {
    return (
      <Page.Container>
        <Page.Content className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-bodhi-teal" />
        </Page.Content>
      </Page.Container>
    );
  }

  // No incident selected: show dashboard
  if (!activeIncident) {
    return (
      <RestorationRoomErrorBoundary>
        <IncidentDashboard
          incidents={incidents}
          schoolIncidents={schoolIncidents}
          onNewIncident={handleNewIncident}
          onSelectIncident={handleSelectIncidentWithFlush}
          onSelectSchoolIncident={handleSelectSchoolIncident}
          onDeleteIncident={handleDeleteIncident}
          onStatusChange={handleIncidentStatusChange}
          onResetAll={handleResetAll}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          districtConfig={districtConfig}
          onSaveDistrictConfig={setDistrictConfig}
          schoolId={schoolId}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </RestorationRoomErrorBoundary>
    );
  }

  // While the leader hasn't picked a grade yet, fall back to the
  // middle-school band for guidance/reflection lookups so the chat has
  // *something* to render. We do NOT mirror this fallback into the
  // visible header — the user-facing "· Grade X" only appears once a
  // real grade is captured (see header below).
  const lookupGrade = effectiveGrade(activeIncident.grade_level);
  const gradeGuidance = GRADE_GUIDANCE[lookupGrade];
  const reflectionPrompts = activeIncident.repeat_incident
    ? {
        ...STUDENT_REFLECTION_PROMPTS[lookupGrade],
        intro: STUDENT_REFLECTION_REPEAT_INTRO[lookupGrade],
        prompts: [
          ...STUDENT_REFLECTION_PROMPTS[lookupGrade].prompts,
          ...STUDENT_REFLECTION_REPEAT_EXTRA_PROMPTS[lookupGrade],
        ],
        closing: STUDENT_REFLECTION_REPEAT_CLOSING[lookupGrade],
      }
    : STUDENT_REFLECTION_PROMPTS[lookupGrade];

  // Active incident
  return (
    <RestorationRoomErrorBoundary>
      <Page.Container>
        <Page.Header className="text-left md:!pt-5 md:!pb-2">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <button
                onClick={() => { flushPending(); discardActiveDraft(); setActiveIncidentId(null); setSaveState("idle"); }}
                className="text-white/60 text-xs hover:text-white mb-1 flex items-center gap-1"
              >
                ← All incidents
              </button>
              <h1 className="text-xl md:text-2xl font-semibold text-white font-literata leading-tight">
                {activeIncident.student_identifier || "New Incident"}
                {isGradeSet(activeIncident.grade_level)
                  ? ` · Grade ${activeIncident.grade_level}`
                  : ""}
              </h1>
              <p className="text-white/60 text-xs mt-0.5">
                {summarizeIncident(activeIncident)}
              </p>
              {/* Read-only badge */}
              {isReadOnly && (
                <p className="text-amber-300 text-xs mt-1">
                  Viewing {activeIncident.creator_name ?? "colleague"}'s incident · read-only
                </p>
              )}
              {/* Save indicator + scope toggle */}
              {!isReadOnly && (
                <>
                  <p className="text-white/50 text-[10px] mt-0.5">
                    {saveState === "saving" && "Saving..."}
                    {saveState === "saved" && "Saved ✓"}
                    {saveState === "error" && "Error saving"}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const next: ShareScope =
                        (activeIncident.share_scope ?? "school") === "private"
                          ? "school"
                          : "private";
                      updateIncident({ share_scope: next });
                    }}
                    className="inline-flex items-center gap-1 text-[10px] mt-1 transition-colors text-white/50 hover:text-white/80"
                    title={
                      (activeIncident.share_scope ?? "school") === "private"
                        ? "Private — only you can see this incident. Click to share with school."
                        : "Shared with school — colleagues can see this incident. Click to make private."
                    }
                  >
                    {(activeIncident.share_scope ?? "school") === "private" ? (
                      <>
                        <Lock className="w-3 h-3" aria-hidden />
                        Private
                      </>
                    ) : (
                      <>
                        <Users className="w-3 h-3" aria-hidden />
                        Shared with school
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
              <div className="flex gap-2 flex-wrap justify-start sm:justify-end">
                {activeIncident.status !== "closed" && !isReadOnly && (
                  <Button
                    size="sm"
                    className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 border-0"
                    onClick={handleCloseIncident}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Mark Complete
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-white/40 bg-white/10 hover:bg-white/20"
                  onClick={handleNewIncident}
                >
                  + New Incident
                </Button>
              </div>
            </div>
          </div>

          {/* Stage progress — same StageTracker the chat uses below, so
              completed/active/locked states match in both places. The
              tracker is gated: leaders can revisit any completed stage
              but can't skip into a stage whose prerequisites aren't met. */}
          <div className="mt-3 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
            <StageTracker
              variant="pills"
              incident={activeIncident}
              activeStage={activeStage}
              onJump={(stage) => chatHandleRef.current?.jumpToStage(stage)}
              isReadOnly={isReadOnly}
            />

            <div className="inline-flex self-start lg:self-auto gap-2 flex-wrap rounded-lg border border-white/35 bg-white/5 p-1.5">
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/40 bg-white/10 hover:bg-white/20"
                onClick={() => setDocsOpen(true)}
                aria-label="Open documents for this incident"
              >
                <FolderOpen className="w-4 h-4 mr-1" />
                Documents
                {activeAttachments.length + incidentSnapshots.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white/20 text-[10px] font-semibold">
                    {activeAttachments.length + incidentSnapshots.length}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/40 bg-white/10 hover:bg-white/20"
                onClick={() => {
                  chatHandleRef.current?.openArtifact("case-record");
                }}
                title="Open the documentation sheet (Notes, AI narrative, polish, download packet)"
                aria-label="Open case record and documentation"
              >
                <FileCheck2 className="w-4 h-4 mr-1" />
                Case record
              </Button>
            </div>
          </div>
        </Page.Header>

        <Page.Content className="md:!pt-2 md:!mt-0 !space-y-3">
          <RestorationRoomChat
            ref={chatHandleRef}
            incident={activeIncident}
            gradeGuidance={gradeGuidance}
            reflectionPrompts={reflectionPrompts}
            buildConversationGuide={buildConversationGuide}
            downloadIncidentPacketPDF={downloadIncidentPacketPDF}
            onUpdateIncident={updateIncident}
            onCloseIncident={handleCloseIncident}
            onNewIncident={handleNewIncident}
            onBackToDashboard={() => { flushPending(); discardActiveDraft(); setActiveIncidentId(null); setSaveState("idle"); }}
            onStageChange={setActiveStage}
            isReadOnly={isReadOnly}
            isUnsavedDraft={
              !!activeIncident && draftIdsRef.current.has(activeIncident.id)
            }
            onAttachmentsChange={setActiveAttachments}
          />
        </Page.Content>
      </Page.Container>

      <IncidentDocumentsPanel
        open={docsOpen}
        onOpenChange={setDocsOpen}
        incident={activeIncident}
        attachments={activeAttachments}
        snapshots={incidentSnapshots}
        snapshotsLoading={snapshotsLoading}
        savingSnapshotKind={savingSnapshotKind}
        onUpload={async (file) => {
          await chatHandleRef.current?.uploadFile(file);
        }}
        onView={(fileId) => chatHandleRef.current?.viewAttachment(fileId)}
        onRemove={async (fileId) => {
          await chatHandleRef.current?.removeAttachment(fileId);
        }}
        onToggleUploadScope={async (fileId, newScope) => {
          try {
            await storageService.shareFile(fileId, newScope);
            // Update the chat's mirror so the file's badge flips
            // immediately without a re-fetch.
            setActiveAttachments((prev) =>
              prev.map((a) =>
                a.id === fileId ? { ...a, share_scope: newScope } : a,
              ),
            );
            toast.success(
              newScope === "school"
                ? "File now visible to your school"
                : "File is private to you",
            );
          } catch (err) {
            console.error("Failed to update file scope:", err);
            toast.error("Couldn't update sharing.");
          }
        }}
        onSaveSnapshot={async (kind) => {
          if (kind !== "incident-packet") {
            toast.info("Snapshot saving is available for the incident packet.");
            return;
          }
          setSavingSnapshotKind(kind);
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Not authenticated");
            const { data: profile } = await supabase
              .from("profiles")
              .select("school_id, district_id")
              .eq("id", user.id)
              .maybeSingle();
            const resolvedSchoolId = profile?.school_id ?? undefined;
            const districtId = profile?.district_id ?? undefined;

            const { blob, filename } = await buildIncidentPacketBlob(
              activeIncident,
              buildConversationGuide,
            );
            const file = new File([blob], filename, {
              type: "application/pdf",
            });

            const saved = await storageService.uploadFileToLibrary(file, {
              shareScope: "school",
              category: "restoration",
              schoolId: resolvedSchoolId,
              districtId,
              contextType: "restoration_incident",
              contextId: activeIncident.id,
            });
            setIncidentSnapshots((prev) => [...prev, saved]);
            toast.success("Snapshot saved to school library");
          } catch (err) {
            console.error("Failed to save snapshot:", err);
            toast.error("Couldn't save snapshot.");
          } finally {
            setSavingSnapshotKind(null);
          }
        }}
        onViewSnapshot={async (filePath) => {
          try {
            const url = await storageService.getSignedUrlFromPath(filePath);
            window.open(url, "_blank", "noopener,noreferrer");
          } catch (err) {
            console.error("Failed to open snapshot:", err);
            toast.error("Couldn't open that snapshot.");
          }
        }}
        onToggleSnapshotScope={async (fileId, newScope) => {
          try {
            await storageService.setSnapshotShareScope(fileId, newScope);
            setIncidentSnapshots((prev) =>
              prev.map((s) =>
                s.id === fileId
                  ? {
                      ...s,
                      share_scope: newScope,
                      is_shared: newScope === "school",
                    }
                  : s,
              ),
            );
            toast.success(
              newScope === "school"
                ? "Snapshot now visible to your school"
                : "Snapshot is private to you",
            );
          } catch (err) {
            console.error("Failed to update snapshot scope:", err);
            toast.error("Couldn't update sharing.");
          }
        }}
        onDeleteSnapshot={async (fileId) => {
          try {
            await storageService.deleteSnapshot(fileId);
            setIncidentSnapshots((prev) => prev.filter((s) => s.id !== fileId));
            toast.success("Snapshot deleted");
          } catch (err) {
            console.error("Failed to delete snapshot:", err);
            toast.error("Couldn't delete that snapshot.");
          }
        }}
        onOpenArtifact={(kind) => {
          chatHandleRef.current?.openArtifact(kind);
          setDocsOpen(false);
        }}
        isReadOnly={isReadOnly}
        currentUserId={currentUserId}
        isUnsavedDraft={
          !!activeIncident && draftIdsRef.current.has(activeIncident.id)
        }
      />

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all incidents?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all incident records from this device. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bodhi-gold text-white hover:bg-bodhi-gold/90"
              onClick={() => {
                handleResetAll();
                setShowResetConfirm(false);
              }}
            >
              Clear all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RestorationRoomErrorBoundary>
  );
};
